import type React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import CollectionCenterDropdown from "../../../components/CollectionCenterDropdown";
import DateRangeSelector from "../../../components/DateRangeSelector";
import { fetchProfileData } from "../../../redux/reducers/profileSlice";

import MedicalDashboardChart from "../dashboardChart";
import DoctorWiseChart from "../doctorWiseChart";
import StatusMetrics from "../statusCards/StatusCards";
import ProfitLossSummary from "../statusCards/ProfitLossSummary";
import { toast } from "react-toastify";

const formatDateLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getToday = () => formatDateLocal(new Date());

const getThisMonthDates = () => {
  const today = new Date();
  return {
    from: formatDateLocal(new Date(today.getFullYear(), today.getMonth(), 1)),
    to: formatDateLocal(today),
  };
};

const LabDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const profile = useSelector((state: any) => state.profileSlice.profile);
  const labPermissions = profile?.data?.accessConfigurations?.Lab;

  const [data, setData] = useState<any>({
    samples: 0,
    tests: 0,
    grossAmount: 0,
    earning: 0,
    dueAmount: 0,
    discount: 0,
  });
  const [selectedCollectionCenterId, setSelectedCollectionCenterId] =
    useState<string>("");
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [doctorWiseLoading, setDoctorWiseLoading] = useState(false);
  const [centerWiseLoading, setCenterWiseLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [doctorWiseData, setDoctorWiseData] = useState<any[]>([]);
  const [centerWiseData, setCenterWiseData] = useState<any[]>([]);
  const todayStr = getToday();
  const thisMonth = getThisMonthDates();
  const [dateRange, setDateRange] = useState<string>("Today");
  const [dateRangeChart, setDateRangeChart] = useState<string>("This Month");
  const [dateRangeUserWise, setDateRangeUserWise] =
    useState<string>("Today");
  const [dateRangeCenterWise, setDateRangeCenterWise] =
    useState<string>("Today");
  const [fromDate, setFromDate] = useState<string>(todayStr);
  const [toDate, setToDate] = useState<string>(todayStr);
  const [fromDateChart, setFromDateChart] = useState<string>(thisMonth.from);
  const [toDateChart, setToDateChart] = useState<string>(thisMonth.to);
  const [fromDateUserWise, setFromDateUserWise] = useState<string>(todayStr);
  const [toDateUserWise, setToDateUserWise] = useState<string>(todayStr);
  const [fromDateCenterWise, setFromDateCenterWise] = useState<string>(todayStr);
  const [toDateCenterWise, setToDateCenterWise] = useState<string>(todayStr);
  const [fromDateProfitLoss, setFromDateProfitLoss] = useState<string>(todayStr);
  const [toDateProfitLoss, setToDateProfitLoss] = useState<string>(todayStr);

  const [profitLossData, setProfitLossData] = useState<any>(null);
  const [profitLossLoading, setProfitLossLoading] = useState(false);
  const [dateRangeProfitLoss, setDateRangeProfitLoss] = useState<string>("Today");

  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });

  const collectionCenterQuery =
    selectedCollectionCenterId && selectedCollectionCenterId !== "all"
      ? { collectionCenterId: selectedCollectionCenterId }
      : {};

  // Fetch user profile on component mount
  useEffect(() => {
    dispatch(fetchProfileData() as any);
  }, [dispatch]);

  // Fetch data whenever fromDate, toDate, or selectedCollectionCenterId changes
  useEffect(() => {
    if (!fromDate || !toDate) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAB_URL}/dashboard/getLabState`,
          {
            params: {
              fromDate,
              toDate,
              ...collectionCenterQuery,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setData(response?.data?.data);
      } catch (error:any) {
        console.error("Error fetching data:", error);
        toast.error(error?.response?.data?.message || "Error fetching data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [fromDate, toDate, selectedCollectionCenterId]);

  // Fetch chart data
  useEffect(() => {
    if (!fromDateChart || !toDateChart) return;

    const fetchChartData = async () => {
      setChartLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAB_URL}/dashboard/topTestPerformed`,
          {
            params: {
              fromDate: fromDateChart,
              toDate: toDateChart,
              ...collectionCenterQuery,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setChartData(response?.data?.data);
      } catch (error:any) {
        console.error("Error fetching chart data:", error);
        toast.error(error?.response?.data?.message || "Error fetching chart data");
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [fromDateChart, toDateChart, selectedCollectionCenterId]);

  // Fetch doctor-wise lab state data
  useEffect(() => {
    if (
      !fromDateUserWise ||
      !toDateUserWise ||
      !labPermissions?.show_user_wize_graph
    )
      return;

    const fetchDoctorWiseData = async () => {
      setDoctorWiseLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAB_URL}/dashboard/getLabStateUserWize`,
          {
            params: {
              fromDate: fromDateUserWise,
              toDate: toDateUserWise,
              ...collectionCenterQuery,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setDoctorWiseData(response?.data?.data || []);
      } catch (error:any) {
        console.error("Error fetching doctor-wise data:", error);
        toast.error(error?.response?.data?.message || "Error fetching doctor-wise data");
        setDoctorWiseData([]);
      } finally {
        setDoctorWiseLoading(false);
      }
    };

    fetchDoctorWiseData();
  }, [
    fromDateUserWise,
    toDateUserWise,
    selectedCollectionCenterId,
    labPermissions?.show_user_wize_graph,
  ]);

  // Fetch center-wise lab state data
  useEffect(() => {
    if (
      !fromDateCenterWise ||
      !toDateCenterWise ||
      !labPermissions?.show_center_wise_graph
    )
      return;

    const fetchCenterWiseData = async () => {
      setCenterWiseLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAB_URL}/dashboard/getLabStateCenterWise`,
          {
            params: {
              fromDate: fromDateCenterWise,
              toDate: toDateCenterWise,
              ...collectionCenterQuery,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setCenterWiseData(response?.data?.data || []);
      } catch (error) {
        console.error("Error fetching center-wise data:", error);
        setCenterWiseData([]);
      } finally {
        setCenterWiseLoading(false);
      }
    };
    fetchCenterWiseData();
  }, [
    fromDateCenterWise,
    toDateCenterWise,
    selectedCollectionCenterId,
    labPermissions?.show_center_wise_graph,
  ]);

  // Fetch profit & loss summary data
  useEffect(() => {
    if (!fromDateProfitLoss || !toDateProfitLoss) return;

    const fetchProfitLossData = async () => {
      setProfitLossLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LAB_URL}/dashboard/getProfitLossSummary`,
          {
            params: {
              fromDate: fromDateProfitLoss,
              toDate: toDateProfitLoss,
              ...collectionCenterQuery,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setProfitLossData(response?.data?.data);
      } catch (error: any) {
        console.error("Error fetching profit loss data:", error);
        toast.error(
          error?.response?.data?.message || "Error fetching profit loss data"
        );
      } finally {
        setProfitLossLoading(false);
      }
    };

    fetchProfitLossData();
  }, [fromDateProfitLoss, toDateProfitLoss, selectedCollectionCenterId]);

  return (
    <div className="bg-gray-100 has-[overflow-y:scroll] p-4 overflow-hidden">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white rounded-lg shadow-sm p-4">
          <h1 className="text-2xl font-bold text-gray-800">Lab Dashboard</h1>
          <div className="flex items-center gap-4">
            <div style={{ minWidth: "200px" }}>
              <CollectionCenterDropdown
                value={selectedCollectionCenterId}
                onChange={(value: string) =>
                  setSelectedCollectionCenterId(value)
                }
                label="Collection Center"
                size="small"
                required={false}
                autoSelect={true}
                saveToStorage={true}
                showAllOption={true}
              />
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading main dashboard data...</p>
          </div>
        )}

        {/* Dashboard Layout */}
        {!loading && (
          <div className="space-y-6">
            {/* Top Row: Metrics and Profit/Loss Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column: Status Metrics */}
              <div className="bg-white rounded-lg shadow-sm">
                <StatusMetrics
                  data={data}
                  dataRange={dateRange}
                  fromDate={fromDate}
                  toDate={toDate}
                  onDateRangeChange={setDateRange}
                  onFromDateChange={setFromDate}
                  onToDateChange={setToDate}
                />
              </div>

              {/* Right Column: Profit & Loss Summary */}
              <div className="bg-white rounded-lg shadow-sm">
                {profitLossLoading ? (
                  <div className="flex items-center justify-center h-full p-8 min-h-[200px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <ProfitLossSummary
                    data={profitLossData}
                    dataRange={dateRangeProfitLoss}
                    fromDate={fromDateProfitLoss}
                    toDate={toDateProfitLoss}
                    onDateRangeChange={setDateRangeProfitLoss}
                    onFromDateChange={setFromDateProfitLoss}
                    onToDateChange={setToDateProfitLoss}
                  />
                )}
              </div>
            </div>

            {/* Middle Section: Doctor-wise and Center-wise Charts */}
            {((doctorWiseData &&
              doctorWiseData.length > 0 &&
              labPermissions?.show_user_wize_graph) ||
              (centerWiseData &&
                centerWiseData.length > 0 &&
                labPermissions?.show_center_wise_graph)) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column: Doctor-wise Chart */}
                {(doctorWiseData || doctorWiseLoading) &&
                  labPermissions?.show_user_wize_graph && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      {doctorWiseLoading ? (
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <p className="ml-4 text-gray-600">
                            Loading doctor-wise data...
                          </p>
                        </div>
                      ) : (
                        <DoctorWiseChart
                          data={doctorWiseData}
                          dateRange={dateRangeUserWise}
                          fromDate={fromDateUserWise}
                          toDate={toDateUserWise}
                          onDateRangeChange={setDateRangeUserWise}
                          onFromDateChange={setFromDateUserWise}
                          onToDateChange={setToDateUserWise}
                        />
                      )}
                    </div>
                  )}

                {/* Right Column: Center-wise bar Chart */}
                {((centerWiseData && centerWiseData.length > 0) ||
                  centerWiseLoading) &&
                  labPermissions?.show_center_wise_graph && (
                    <div className="bg-white rounded-lg shadow-sm p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-700">
                          Collection Center-wise Lab Revenue{" "}
                          {dateRangeCenterWise === "custom"
                            ? `between ${fromDateCenterWise} and ${toDateCenterWise}`
                            : `${dateRangeCenterWise}`}
                        </h3>
                        <DateRangeSelector
                          preset="lab"
                          compact
                          dateRange={dateRangeCenterWise}
                          fromDate={fromDateCenterWise}
                          toDate={toDateCenterWise}
                          onDateRangeChange={setDateRangeCenterWise}
                          onFromDateChange={setFromDateCenterWise}
                          onToDateChange={setToDateCenterWise}
                        />
                      </div>

                      {centerWiseLoading ? (
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <p className="ml-4 text-gray-600">
                            Loading center-wise data...
                          </p>
                        </div>
                      ) : centerWiseData && centerWiseData.length > 0 ? (
                        <>
                          <div className="w-full">
                            <div className="relative">
                              <svg
                                width="100%"
                                height="300"
                                viewBox="0 0 500 300"
                                className="mx-auto"
                              >
                                {(() => {
                                  const colors = [
                                    "#3B82F6",
                                    "#10B981",
                                    "#F59E0B",
                                    "#EF4444",
                                    "#8B5CF6",
                                    "#06B6D4",
                                    "#F97316",
                                  ];

                                  const padding = {
                                    top: 50,
                                    right: 40,
                                    bottom: 90,
                                    left: 60,
                                  };
                                  const chartWidth =
                                    500 - padding.left - padding.right;
                                  const chartHeight =
                                    300 - padding.top - padding.bottom;

                                  const getSharePct = (item: any) =>
                                    item.collectionCenter?.share === null ||
                                    item.collectionCenter?.share === undefined
                                      ? 100
                                      : Number(item.collectionCenter.share);

                                  const getSharePaid = (item: any) =>
                                    ((item.paidAmount || 0) * getSharePct(item)) /
                                    100;

                                  const rawMaxValue = Math.max(
                                    ...centerWiseData.flatMap(item => [
                                      item.paidAmount || 0,
                                      getSharePaid(item),
                                    ])
                                  );
                                  const maxValue =
                                    rawMaxValue > 0 ? rawMaxValue : 100;

                                  return (
                                    <g>
                                      {[0, 0.25, 0.5, 0.75, 1].map(
                                        (ratio, i) => {
                                          const value = maxValue * (1 - ratio);
                                          const formatValue = (val: number) => {
                                            if (val >= 1000000) {
                                              return `${(val / 1000000).toFixed(
                                                1
                                              )}M`;
                                            } else if (val >= 10000) {
                                              return `${(val / 1000).toFixed(
                                                0
                                              )}K`;
                                            } else if (val >= 1000) {
                                              return `${(val / 1000).toFixed(
                                                1
                                              )}K`;
                                            } else {
                                              return val.toFixed(0);
                                            }
                                          };

                                          return (
                                            <g key={i}>
                                              <line
                                                x1={padding.left}
                                                y1={
                                                  padding.top +
                                                  chartHeight * ratio
                                                }
                                                x2={padding.left + chartWidth}
                                                y2={
                                                  padding.top +
                                                  chartHeight * ratio
                                                }
                                                stroke="#e5e7eb"
                                                strokeWidth="1"
                                              />
                                              <text
                                                x={padding.left - 10}
                                                y={
                                                  padding.top +
                                                  chartHeight * ratio +
                                                  4
                                                }
                                                textAnchor="end"
                                                fontSize="12"
                                                fill="#6b7280"
                                              >
                                                {formatValue(value)}
                                              </text>
                                            </g>
                                          );
                                        }
                                      )}

                                      <line
                                        x1={padding.left}
                                        y1={padding.top + chartHeight}
                                        x2={padding.left + chartWidth}
                                        y2={padding.top + chartHeight}
                                        stroke="#374151"
                                        strokeWidth="2"
                                      />

                                      <line
                                        x1={padding.left}
                                        y1={padding.top}
                                        x2={padding.left}
                                        y2={padding.top + chartHeight}
                                        stroke="#374151"
                                        strokeWidth="2"
                                      />

                                      {/* Legend */}
                                      <g>
                                        <rect
                                          x={padding.left + chartWidth - 150}
                                          y={8}
                                          width="10"
                                          height="10"
                                          fill="#3B82F6"
                                          rx="2"
                                        />
                                        <text
                                          x={padding.left + chartWidth - 136}
                                          y={17}
                                          fontSize="10"
                                          fill="#374151"
                                        >
                                          Paid
                                        </text>
                                        <rect
                                          x={padding.left + chartWidth - 90}
                                          y={8}
                                          width="10"
                                          height="10"
                                          fill="#e65100"
                                          rx="2"
                                        />
                                        <text
                                          x={padding.left + chartWidth - 76}
                                          y={17}
                                          fontSize="10"
                                          fill="#374151"
                                        >
                                          Share Paid
                                        </text>
                                      </g>

                                      {centerWiseData.map((item, index) => {
                                        const groupWidth =
                                          chartWidth / centerWiseData.length;
                                        const barGap = 4;
                                        const barWidth =
                                          (groupWidth * 0.7 - barGap) / 2;
                                        const groupStart =
                                          padding.left +
                                          index * groupWidth +
                                          groupWidth * 0.15;
                                        const paidAmount = item.paidAmount || 0;
                                        const centerShare = getSharePct(item);
                                        const sharePaid = getSharePaid(item);
                                        const paidBarHeight =
                                          (paidAmount / maxValue) * chartHeight;
                                        const shareBarHeight =
                                          (sharePaid / maxValue) * chartHeight;
                                        const paidY =
                                          padding.top +
                                          chartHeight -
                                          paidBarHeight;
                                        const shareY =
                                          padding.top +
                                          chartHeight -
                                          shareBarHeight;
                                        const paidColor =
                                          colors[index % colors.length];
                                        const shareColor = "#e65100";

                                        const centerName =
                                          item.collectionCenter
                                            ?.collectionCenterName ||
                                          (item._id === null
                                            ? "Unknown Center"
                                            : `Center ${index + 1}`);

                                        const showTooltip = (
                                          e: React.MouseEvent
                                        ) => {
                                          const tooltipContent = `${centerName} (${centerShare}%)\n${
                                            item.collectionCenter?.address ||
                                            "No address"
                                          }\nShare: ${centerShare}%\nPaid Amount: ${paidAmount.toLocaleString()}\nShare Paid: ${sharePaid.toLocaleString()}\nTotal Revenue: ${(
                                            item.grossAmount || 0
                                          ).toLocaleString()}\nDue: ${(
                                            item.dueAmount || 0
                                          ).toLocaleString()}\nPatients: ${
                                            item.patientCount || 0
                                          }`;
                                          setTooltip({
                                            visible: true,
                                            x: e.clientX,
                                            y: e.clientY,
                                            content: tooltipContent,
                                          });
                                        };

                                        return (
                                          <g key={item._id || index}>
                                            {/* Paid Amount bar */}
                                            <rect
                                              x={groupStart}
                                              y={paidY}
                                              width={barWidth}
                                              height={paidBarHeight}
                                              fill={paidColor}
                                              className="hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                                              style={{
                                                filter:
                                                  "drop-shadow(2px 2px 4px rgba(0,0,0,0.1))",
                                              }}
                                              onMouseEnter={showTooltip}
                                              onMouseMove={e => {
                                                setTooltip(prev => ({
                                                  ...prev,
                                                  x: e.clientX,
                                                  y: e.clientY,
                                                }));
                                              }}
                                              onMouseLeave={() => {
                                                setTooltip(prev => ({
                                                  ...prev,
                                                  visible: false,
                                                }));
                                              }}
                                            />

                                            {/* Share Paid bar */}
                                            <rect
                                              x={groupStart + barWidth + barGap}
                                              y={shareY}
                                              width={barWidth}
                                              height={shareBarHeight}
                                              fill={shareColor}
                                              className="hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                                              style={{
                                                filter:
                                                  "drop-shadow(2px 2px 4px rgba(0,0,0,0.1))",
                                              }}
                                              onMouseEnter={showTooltip}
                                              onMouseMove={e => {
                                                setTooltip(prev => ({
                                                  ...prev,
                                                  x: e.clientX,
                                                  y: e.clientY,
                                                }));
                                              }}
                                              onMouseLeave={() => {
                                                setTooltip(prev => ({
                                                  ...prev,
                                                  visible: false,
                                                }));
                                              }}
                                            />

                                            <text
                                              x={groupStart + barWidth / 2}
                                              y={paidY - 6}
                                              textAnchor="middle"
                                              fontSize="9"
                                              fontWeight="600"
                                              fill={paidColor}
                                            >
                                              {paidAmount.toLocaleString()}
                                            </text>

                                            <text
                                              x={
                                                groupStart +
                                                barWidth +
                                                barGap +
                                                barWidth / 2
                                              }
                                              y={shareY - 6}
                                              textAnchor="middle"
                                              fontSize="9"
                                              fontWeight="700"
                                              fill={shareColor}
                                            >
                                              {sharePaid.toLocaleString()}
                                            </text>

                                            <text
                                              x={
                                                groupStart +
                                                barWidth +
                                                barGap / 2
                                              }
                                              y={padding.top + chartHeight + 18}
                                              textAnchor="middle"
                                              fontSize="11"
                                              fill="#374151"
                                              className="max-w-[80px]"
                                            >
                                              {centerName.length > 10
                                                ? centerName.substring(0, 10) +
                                                  "..."
                                                : centerName}
                                            </text>

                                            <text
                                              x={
                                                groupStart +
                                                barWidth +
                                                barGap / 2
                                              }
                                              y={padding.top + chartHeight + 32}
                                              textAnchor="middle"
                                              fontSize="10"
                                              fontWeight="700"
                                              fill="#e65100"
                                            >
                                              {`(${centerShare}%)`}
                                            </text>

                                            <text
                                              x={
                                                groupStart +
                                                barWidth +
                                                barGap / 2
                                              }
                                              y={padding.top + chartHeight + 46}
                                              textAnchor="middle"
                                              fontSize="9"
                                              fill="#6b7280"
                                            >
                                              {(
                                                item.collectionCenter
                                                  ?.address || "No address"
                                              ).length > 15
                                                ? (
                                                    item.collectionCenter
                                                      ?.address || "No address"
                                                  ).substring(0, 15) + "..."
                                                : item.collectionCenter
                                                    ?.address || "No address"}
                                            </text>
                                          </g>
                                        );
                                      })}

                                      <text
                                        x={padding.left + chartWidth / 2}
                                        y={28}
                                        textAnchor="middle"
                                        fontSize="14"
                                        fontWeight="600"
                                        fill="#374151"
                                      >
                                        Paid vs Share Paid by Collection Centers
                                      </text>

                                      <text
                                        x={30}
                                        y={padding.top + chartHeight / 2}
                                        textAnchor="middle"
                                        fontSize="12"
                                        fill="#6b7280"
                                        transform={`rotate(-90, 20, ${
                                          padding.top + chartHeight / 2
                                        })`}
                                      >
                                        Amount
                                      </text>
                                    </g>
                                  );
                                })()}
                              </svg>
                            </div>

                            {tooltip.visible && (
                              <div
                                className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-3 pointer-events-none"
                                style={{
                                  left: tooltip.x + 10,
                                  top: tooltip.y - 10,
                                  transform: "translate(0, -100%)",
                                }}
                              >
                                <div className="text-sm">
                                  {tooltip.content
                                    .split("\n")
                                    .map((line, index) => (
                                      <div
                                        key={index}
                                        className={`${
                                          index === 0
                                            ? "font-semibold text-gray-800"
                                            : index === 1
                                            ? "text-gray-600 text-xs mb-1"
                                            : "text-gray-700"
                                        }`}
                                      >
                                        {line}
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="mt-6 pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-3 gap-4">
                              <div className="text-center">
                                <p className="text-2xl font-bold text-gray-900">
                                  {centerWiseData.reduce(
                                    (sum: number, item: any) =>
                                      sum + (item.patientCount || 0),
                                    0
                                  )}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Total Patients
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-green-600">
                                  {centerWiseData
                                    .reduce(
                                      (sum: number, item: any) =>
                                        sum + (item.paidAmount || 0),
                                      0
                                    )
                                    .toLocaleString()}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Total Paid
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-2xl font-bold text-blue-600">
                                  {centerWiseData
                                    .reduce(
                                      (sum: number, item: any) =>
                                        sum + (item.grossAmount || 0),
                                      0
                                    )
                                    .toLocaleString()}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Total Revenue
                                </p>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-64 text-gray-500">
                          <p>
                            No center-wise data available for the selected date
                            range
                          </p>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}

            {/* Bottom Row: Top Tests Chart */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              {chartLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="ml-4 text-gray-600">Loading chart data...</p>
                </div>
              ) : (
                <MedicalDashboardChart
                  fromDate={fromDateChart}
                  toDate={toDateChart}
                  data={chartData}
                  dateRange={dateRangeChart}
                  onDateRangeChange={setDateRangeChart}
                  onFromDateChange={setFromDateChart}
                  onToDateChange={setToDateChart}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



export default LabDashboard;

